/**
 * App router — Sprint 2.2
 *
 * Routes:
 *   /                   → redirect /login
 *   /login              → Login
 *   /register/step-1    → RegisterStep1 (email + password, Google)
 *   /register/step-2    → RegisterStep2 (profile: displayName + locale)  [protected]
 *   /register/step-3    → RegisterStep3 (org: create or join)             [protected]
 *   /register/step-4    → RegisterStep4 (email confirm, polls verified)   [protected]
 *   /auth/register      → redirect → /register/step-1 (legacy compat)
 *   /dashboard          → Dashboard                                        [protected]
 *   *                   → redirect /login
 */

import { createBrowserRouter, Navigate } from 'react-router';
import { Suspense, lazy } from 'react';
import { ProtectedRoute } from '@/components/auth/ProtectedRoute';
import { Spinner } from '@/components/atoms/Spinner';

// Lazy-loaded pages
const Login = lazy(() => import('./pages/auth/Login'));
const Dashboard = lazy(() => import('./pages/dashboard/Dashboard'));
const RegisterStep1 = lazy(() => import('./pages/auth/RegisterStep1'));
const RegisterStep2 = lazy(() => import('./pages/auth/RegisterStep2'));
const RegisterStep3 = lazy(() => import('./pages/auth/RegisterStep3'));
const RegisterStep4 = lazy(() => import('./pages/auth/RegisterStep4'));
const ForgotPassword = lazy(() => import('./pages/auth/ForgotPassword'));
const ResetPassword = lazy(() => import('./pages/auth/ResetPassword'));

// ── Full-page loading fallback ────────────────────────────────────────────────

function PageLoader() {
  return (
    <div
      className="flex h-screen w-full items-center justify-center"
      style={{ background: 'var(--auth-surface-page)' }}
    >
      <Spinner size="lg" label="Loading" />
    </div>
  );
}

// ── Router definition ─────────────────────────────────────────────────────────

export const router = createBrowserRouter([
  {
    path: '/',
    element: <Navigate to="/login" replace />,
  },
  {
    path: '/login',
    element: (
      <Suspense fallback={<PageLoader />}>
        <Login />
      </Suspense>
    ),
  },
  {
    // Step 1 is public (no Firebase token required — user is registering)
    path: '/register/step-1',
    element: (
      <Suspense fallback={<PageLoader />}>
        <RegisterStep1 />
      </Suspense>
    ),
  },
  {
    // Steps 2-4 require the user to be signed in (Firebase token present)
    path: '/register/step-2',
    element: (
      <ProtectedRoute>
        <Suspense fallback={<PageLoader />}>
          <RegisterStep2 />
        </Suspense>
      </ProtectedRoute>
    ),
  },
  {
    path: '/register/step-3',
    element: (
      <ProtectedRoute>
        <Suspense fallback={<PageLoader />}>
          <RegisterStep3 />
        </Suspense>
      </ProtectedRoute>
    ),
  },
  {
    path: '/register/step-4',
    element: (
      <ProtectedRoute>
        <Suspense fallback={<PageLoader />}>
          <RegisterStep4 />
        </Suspense>
      </ProtectedRoute>
    ),
  },
  {
    path: '/auth/forgot-password',
    element: (
      <Suspense fallback={<PageLoader />}>
        <ForgotPassword />
      </Suspense>
    ),
  },
  {
    path: '/auth/reset-password',
    element: (
      <Suspense fallback={<PageLoader />}>
        <ResetPassword />
      </Suspense>
    ),
  },
  {
    // Legacy compat: /auth/register → /register/step-1
    path: '/auth/register',
    element: <Navigate to="/register/step-1" replace />,
  },
  {
    path: '/dashboard',
    element: (
      <ProtectedRoute>
        <Suspense fallback={<PageLoader />}>
          <Dashboard />
        </Suspense>
      </ProtectedRoute>
    ),
  },
  {
    // Catch-all: unrecognised paths → /login
    path: '*',
    element: <Navigate to="/login" replace />,
  },
]);
